// Internal error codes with HTTP status code co-located
export const ERROR_CODE_DEFINITIONS = {
  // Authentication errors (401)
  AUTH_NO_SESSION: 401,
  AUTH_SESSION_INVALID: 401,
  AUTH_SESSION_EXPIRED: 401,
  AUTH_USER_NOT_FOUND: 401,
  AUTH_INVALID_CREDENTIALS: 401,
  AUTH_REQUIRED: 401,
  AUTH_OAUTH_FAILED: 401,

  // Authorization errors (403)
  AUTHZ_INSUFFICIENT_PERMISSIONS: 403,
  AUTHZ_NO_RELATIONSHIP: 403,
  AUTHZ_INVALID_RESOURCE_PATH: 403,
  AUTHZ_VALIDATION_FAILED: 403,

  // Bad Request errors (400)
  BAD_REQUEST: 400,
  INVALID_INPUT: 400,

  // Not Found errors (404)
  RESOURCE_NOT_FOUND: 404,

  // Conflict errors (409)
  RESOURCE_CONFLICT: 409,
  DUPLICATE_RESOURCE: 409,

  // Validation errors (422)
  VALIDATION_FAILED: 422,
  VALIDATION_SCHEMA_FAILED: 422,

  // File upload errors (4xx)
  FILE_UPLOAD_METADATA_MISSING: 400,
  FILE_UPLOAD_MISSING_FILE: 400,
  FILE_UPLOAD_INVALID_MIMETYPE: 422,
  FILE_UPLOAD_INVALID_METADATA: 422,
  FILE_UPLOAD_INVALID_TAGS: 400,
  FILE_NOT_FOUND: 404,

  // Rate Limit errors (429)
  RATE_LIMIT_EXCEEDED: 429,

  // Server errors (500)
  INTERNAL_SERVER_ERROR: 500,

  // Bad Gateway / Integration errors (502)
  INTEGRATION_ERROR: 502,
  INTEGRATION_AGRIWEBB_NOT_FOUND: 502,
  INTEGRATION_REAUTH_REQUIRED: 409,
  INTEGRATION_AGRIWEBB_TOKEN_EXCHANGE: 502,
  INTEGRATION_AGRIWEBB_MARKETPLACE_CALLBACK: 502,
  INTEGRATION_AGRIWEBB_GRAPHQL_QUERY: 502,

  // third-party service errors
  GOOGLE_PLACES_API_ERROR: 502,
  GEOSCAPE_API_ERROR: 502,
  GEOSCAPE_INVALID_URL_ERROR: 422,

  // Email service errors (502)
  EMAIL_SEND_FAILED: 502,
  EMAIL_SERVICE_UNAVAILABLE: 502,

  // Service Unavailable errors (503)
  SERVICE_UNAVAILABLE: 503,
  DATABASE_UNAVAILABLE: 503,
} as const
export type ErrorCode = keyof typeof ERROR_CODE_DEFINITIONS

/**
 * Maps internal error codes to public-facing error codes.
 *
 * Public error codes are a stable, small set of codes exposed to clients.
 * They hide implementation details and prevent information leakage.
 *
 * Design principles for public codes:
 * - Few: Small, stable set (~13 codes vs 30+ internal)
 * - Boring: Predictable, no surprises
 * - Actionable: Clients can branch logic on them
 * - Stable: API contract that rarely changes
 * - Secure: Don't expose internal system details
 *
 * @example
 * // Internal code is specific
 * throw new RuminatiError("GOOGLE_PLACES_API_ERROR", "...")
 *
 * // But client only sees
 * { "code": "UPSTREAM_UNAVAILABLE" }
 */
export const INTERNAL_TO_PUBLIC_ERROR_MAP = {
  // Authentication errors -> AUTH_REQUIRED or AUTH_INVALID
  AUTH_NO_SESSION: 'AUTH_REQUIRED',
  AUTH_SESSION_INVALID: 'AUTH_INVALID',
  AUTH_SESSION_EXPIRED: 'AUTH_INVALID',
  AUTH_USER_NOT_FOUND: 'AUTH_INVALID',
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID',
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_OAUTH_FAILED: 'AUTH_INVALID',

  // Authorization errors -> AUTH_FORBIDDEN
  AUTHZ_INSUFFICIENT_PERMISSIONS: 'AUTH_FORBIDDEN',
  AUTHZ_NO_RELATIONSHIP: 'AUTH_FORBIDDEN',
  AUTHZ_INVALID_RESOURCE_PATH: 'AUTH_FORBIDDEN',
  AUTHZ_VALIDATION_FAILED: 'AUTH_FORBIDDEN',

  // Bad Request errors -> INVALID_REQUEST
  BAD_REQUEST: 'INVALID_REQUEST',
  INVALID_INPUT: 'INVALID_REQUEST',

  // Not Found errors -> RESOURCE_NOT_FOUND
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  FILE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  // Note: Consider collapsing AUTHZ_NO_RELATIONSHIP -> RESOURCE_NOT_FOUND in some cases
  // to avoid leaking resource existence information

  // Conflict errors -> CONFLICT
  RESOURCE_CONFLICT: 'CONFLICT',
  DUPLICATE_RESOURCE: 'CONFLICT',

  // Validation errors -> INVALID_FIELD
  VALIDATION_FAILED: 'INVALID_FIELD',
  VALIDATION_SCHEMA_FAILED: 'INVALID_FIELD',
  FILE_UPLOAD_INVALID_MIMETYPE: 'INVALID_FIELD',
  FILE_UPLOAD_INVALID_METADATA: 'INVALID_FIELD',

  // File upload errors -> INVALID_REQUEST or MISSING_FIELD
  FILE_UPLOAD_METADATA_MISSING: 'MISSING_FIELD',
  FILE_UPLOAD_MISSING_FILE: 'MISSING_FIELD',
  FILE_UPLOAD_INVALID_TAGS: 'INVALID_REQUEST',

  // Geoscape URL validation -> INVALID_FIELD
  GEOSCAPE_INVALID_URL_ERROR: 'INVALID_FIELD',

  // Rate Limit errors -> RATE_LIMITED
  RATE_LIMIT_EXCEEDED: 'RATE_LIMITED',

  // Server errors -> INTERNAL_ERROR
  INTERNAL_SERVER_ERROR: 'INTERNAL_ERROR',

  // Integration errors -> CONFLICT or UPSTREAM_UNAVAILABLE
  // Note: These hide which specific integration failed
  INTEGRATION_ERROR: 'UPSTREAM_UNAVAILABLE',
  INTEGRATION_AGRIWEBB_NOT_FOUND: 'UPSTREAM_UNAVAILABLE',
  INTEGRATION_REAUTH_REQUIRED: 'INTEGRATION_REAUTH_REQUIRED',
  INTEGRATION_AGRIWEBB_TOKEN_EXCHANGE: 'UPSTREAM_UNAVAILABLE',
  INTEGRATION_AGRIWEBB_MARKETPLACE_CALLBACK: 'UPSTREAM_UNAVAILABLE',
  INTEGRATION_AGRIWEBB_GRAPHQL_QUERY: 'UPSTREAM_UNAVAILABLE',

  // Third-party service errors -> UPSTREAM_UNAVAILABLE
  GOOGLE_PLACES_API_ERROR: 'UPSTREAM_UNAVAILABLE',
  GEOSCAPE_API_ERROR: 'UPSTREAM_UNAVAILABLE',

  // Email service errors -> UPSTREAM_UNAVAILABLE
  EMAIL_SEND_FAILED: 'UPSTREAM_UNAVAILABLE',
  EMAIL_SERVICE_UNAVAILABLE: 'UPSTREAM_UNAVAILABLE',

  // Service Unavailable errors -> SERVICE_UNAVAILABLE
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  DATABASE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const satisfies Record<ErrorCode, string>

/**
 * Public error codes - derived from the mapping values.
 * These are the stable codes exposed to clients in API responses.
 */
export type PublicErrorCode = (typeof INTERNAL_TO_PUBLIC_ERROR_MAP)[ErrorCode]

/**
 * Get the public-facing error code for a given internal error code
 */
export function getPublicErrorCode(internalCode: ErrorCode): PublicErrorCode {
  return INTERNAL_TO_PUBLIC_ERROR_MAP[internalCode] ?? 'INTERNAL_ERROR'
}

export const AUTH_PUBLIC_ERROR_CODES = [
  'AUTH_REQUIRED',
  'AUTH_INVALID',
] as const satisfies readonly PublicErrorCode[]
